/*
 * 模块: Header
 * 职责: 顶部导航栏组件
 * 依赖: antd, react-router-dom
 * 导出: Header
 * 限制: 文件<280行
 */

import React from 'react';
import { Badge, Dropdown, Avatar, Space, Button, Tooltip, Tag, App } from 'antd';
import {
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  BulbOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';

export interface HeaderProps {
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
}

/**
 * 应用顶部导航栏
 */
export const Header: React.FC<HeaderProps> = ({
  onFullscreenToggle,
  isFullscreen = false,
}) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { message } = App.useApp();



  /**
   * 用户菜单项
   */

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人设置',
      onClick: () => navigate('/settings/profile'),
    },
    {
      key: 'ai-config',
      icon: <BulbOutlined />,
      label: 'AI 配置',
      onClick: () => navigate('/ai-config'),
    },
    {
      type: 'divider',
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: '帮助中心',
      onClick: () => window.open('/help', '_blank'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: async () => {
        const key = 'logout_msg';
        try {
          // message.loading(content, duration, onClose)
          // duration 0 means do not auto close
          const hide = message.loading({ content: '正在退出...', key, duration: 0 });

          await logout();

          hide();
          message.success({ content: '已安全退出', key, duration: 2 });
          navigate('/login');
        } catch (error) {
          message.error({ content: '退出失败，强制跳转', key });
          navigate('/login');
        }
      },
    },
  ];

  /**
   * 通知菜单项
   */
  const notificationItems: MenuProps['items'] = [
    {
      key: 'notification-1',
      label: (
        <div className="notification-item">
          <div className="notification-title">数据分析完成</div>
          <div className="notification-desc">SKU "智能手表" 的分析已完成</div>
          <div className="notification-time">2分钟前</div>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'view-all',
      label: (
        <div style={{ textAlign: 'center', color: '#1890ff' }}>
          查看全部通知
        </div>
      ),
      onClick: () => navigate('/notifications'),
    },
  ];



  return (
    <header className="app-header">
      <div className="header-logo" onClick={() => navigate('/')}>
        <div className="logo-icon">
          <BulbOutlined />
        </div>
        <div className="logo-text">
          <div className="logo-title">
            Amazon Smart Ops
            <Tag className="logo-pill" color="default">
              v2.3
            </Tag>
          </div>
          <div className="logo-subtitle">数据分析平台</div>
        </div>
      </div>

      <div className="header-actions">
        <Space size="middle">
          <Tooltip title={isFullscreen ? '退出全屏' : '全屏模式'}>
            <Button
              type="text"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={onFullscreenToggle}
              className="header-action-btn"
            />
          </Tooltip>

          <Tooltip title="帮助中心">
            <Button
              type="text"
              icon={<QuestionCircleOutlined />}
              onClick={() => navigate('/metrics-guide')}
              className="header-action-btn header-help-btn"
            />
          </Tooltip>

          <Dropdown menu={{ items: notificationItems }} trigger={['click']} placement="bottomRight">
            <Badge count={2} offset={[-5, 5]}>
              <Button type="text" icon={<BellOutlined />} className="header-action-btn" />
            </Badge>
          </Dropdown>

          <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
            <div className="header-user">
              <Avatar size="default" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <div className="user-profile">
                <span className="user-name">
                  {user?.last_name || user?.first_name
                    ? `${user.last_name}${user.first_name}`
                    : (user?.username || '用户')}
                </span>
                <span className="user-role">{user?.role_display || '成员'}</span>
              </div>
            </div>
          </Dropdown>
        </Space>
      </div>
    </header>
  );
};

export default Header;
